import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { Subject } from 'rxjs';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { EmptyStateComponent } from "../../../../../_shared/components/states/empty-state/empty-state.component";
import { PartsAndInventoryService } from '../../services/parts-and-inventory.service';
import { PartsUsageForecastItem, PartsUsageForecastRequest } from '../../models/parts-usage-forecast.model';
import { TranslocoDirective } from '@jsverse/transloco';

@Component({
  selector: 'app-usage-forecast',
  standalone: true,
  imports: [CommonModule, EmptyStateComponent, InfiniteScrollModule, TranslocoDirective],
  templateUrl: './usage-forecast.component.html',
  styleUrl: './usage-forecast.component.scss'
})
export class UsageForecastComponent implements OnInit, OnChanges, OnDestroy {
  @Input() accountIds: number[] = [];
  @Input() accountsLoaded: boolean = false;

  private readonly pageSize: number = 6;

  private partsAndInventoryService = inject(PartsAndInventoryService);
  private errorHandlerService = inject(ErrorHandlerService);

  usageForecastItems: PartsUsageForecastItem[] = [];
  isLoading: boolean = false;
  isLoadingMore: boolean = false;
  hasInitialLoadCompleted: boolean = false;

  private currentPage: number = 1;
  private totalPages: number = 0;
  private hasMoreData: boolean = true;
  private requestSequence: number = 0;
  private loadCancellation: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    if (this.accountsLoaded && !this.hasInitialLoadCompleted) {
      void this.resetAndLoadAsync();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['accountsLoaded'] && this.accountsLoaded && !this.hasInitialLoadCompleted) {
      void this.resetAndLoadAsync();
      return;
    }

    if (changes['accountIds'] && !changes['accountIds'].firstChange && this.accountsLoaded && this.hasInitialLoadCompleted) {
      void this.resetAndLoadAsync();
    }
  }

  ngOnDestroy(): void {
    this.loadCancellation.next();
    this.loadCancellation.complete();
  }

  onScrollDown(): void {
    if (this.hasMoreData && !this.isLoadingMore) {
      void this.loadForecastPageAsync(false);
    }
  }

  private async resetAndLoadAsync(): Promise<void> {
    this.currentPage = 1;
    this.totalPages = 0;
    this.hasMoreData = true;
    this.usageForecastItems = [];
    await this.loadForecastPageAsync(true);
  }

  private async loadForecastPageAsync(isInitialLoad: boolean): Promise<void> {
    if (!isInitialLoad && (!this.hasMoreData || this.isLoadingMore)) {
      return;
    }

    this.loadCancellation.next();
    const requestId = ++this.requestSequence;

    if (isInitialLoad) {
      this.isLoading = true;
    } else {
      this.isLoadingMore = true;
    }

    try {
      const request: PartsUsageForecastRequest = {
        accountIds: this.accountIds ?? [],
        warehouseIds: [],
        categoryIds: [],
        page: this.currentPage,
        pageSize: this.pageSize
      };

      const response = await this.partsAndInventoryService.getPartsUsageForecastAsync(request, this.loadCancellation);

      if (requestId !== this.requestSequence) {
        return;
      }

      const results = response.data?.results ?? [];
      this.usageForecastItems = isInitialLoad ? results : [...this.usageForecastItems, ...results];
      this.totalPages = response.data?.totalPages ?? 0;
      this.hasMoreData = this.currentPage < this.totalPages;

      if (this.hasMoreData) {
        this.currentPage += 1;
      }
    } catch (error: any) {
      if (requestId === this.requestSequence) {
        this.errorHandlerService.handleError(error);
      }
    } finally {
      if (requestId === this.requestSequence) {
        if (isInitialLoad) {
          this.isLoading = false;
        } else {
          this.isLoadingMore = false;
        }
        this.hasInitialLoadCompleted = true;
      }
    }
  }
}
